import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../components/Layout';
import FuelPriceTable from '../components/FuelPriceTable';
import SEOContent from '../components/SEOContent';
import useTheme from '../hooks/useTheme';
import useExchangeRate from '../hooks/useExchangeRate';

const LitersToGallons = () => {
  const { darkMode, toggleDarkMode, cardClass, inputClass } = useTheme();
  const { dollarRate } = useExchangeRate();

  const [liters, setLiters] = useState('');
  const [result, setResult] = useState(null);

  const handleConvert = (value) => {
    setLiters(value);
    if (value && parseFloat(value) > 0) {
      const litersNum = parseFloat(value);
      const gallons = litersNum / 3.78541;
      setResult({
        liters: litersNum,
        gallons: gallons.toFixed(2),
        gallonsRounded: Math.round(gallons * 10) / 10
      });
    } else {
      setResult(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Przelicznik litry na galony - Cena paliwa w USA za litr | Kalkulator</title>
        <meta name="description" content="Przelicz litry na galony i sprawdź cenę paliwa w USA za litr. Kalkulator konwersji z aktualną ceną benzyny w Stanach. Ile kosztuje paliwo w Teksasie?" />
        <meta name="keywords" content="przelicznik litry na galony, cena paliwa w usa za litr, ile kosztuje paliwo w usa, cena paliwa usa, ile kosztuje paliwo w stanach, litry na galony" />
        <link rel="canonical" href="https://galonyilitry.pl/przelicznik-litry-na-galony" />
      </Helmet>

      <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode} lang="pl">
        <div className="max-w-4xl mx-auto pt-20 pb-12 px-4">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 mb-4">
              Przelicznik litry na galony - cena paliwa w USA za litr
            </h1>
            <p className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Przelicz litry na galony i sprawdź ile kosztuje paliwo w USA za litr. 1 litr = 0,264 galona
            </p>
          </div>

          {/* Calculator Card */}
          <div className={`${cardClass} rounded-2xl border p-6 mb-8`}>
            <h2 className={`text-xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Kalkulator konwersji
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Input */}
              <div>
                <label className={`block ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-2 font-medium`}>
                  Litry:
                </label>
                <input
                  type="number"
                  className={`w-full px-4 py-3.5 border rounded-xl shadow-sm focus:outline-none focus:ring-2 transition-all input-green ${inputClass}`}
                  placeholder="Wpisz liczbę litrów"
                  value={liters}
                  onChange={(e) => handleConvert(e.target.value)}
                />
              </div>

              {/* Output */}
              <div>
                <label className={`block ${darkMode ? 'text-slate-300' : 'text-slate-700'} mb-2 font-medium`}>
                  Galony (US):
                </label>
                <div className={`w-full px-4 py-3.5 border rounded-xl ${darkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <span className={`text-xl font-bold ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}>
                    {result ? `${result.gallons} gal` : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Result Details */}
            {result && (
              <div className={`mt-6 p-4 rounded-xl ${darkMode ? 'bg-gradient-to-r from-blue-700 to-cyan-700' : 'bg-gradient-to-r from-blue-600 to-cyan-500'} text-white`}>
                <p className="text-center text-lg">
                  <span className="font-bold">{result.liters}</span> litrów = <span className="font-bold">{result.gallons}</span> galonów
                </p>
                <p className="text-center text-sm mt-1 opacity-80">
                  (w przybliżeniu {result.gallonsRounded} gal)
                </p>
              </div>
            )}

            {/* Quick Reference */}
            <div className={`mt-6 grid grid-cols-2 md:grid-cols-4 gap-3`}>
              {[5, 10, 20, 50].map((lit) => (
                <button
                  key={lit}
                  onClick={() => handleConvert(lit.toString())}
                  className={`p-3 rounded-lg text-center transition-all ${
                    darkMode
                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                      : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                  }`}
                >
                  <div className="font-bold">{lit} L</div>
                  <div className="text-sm opacity-80">{(lit / 3.78541).toFixed(1)} gal</div>
                </button>
              ))}
            </div>
          </div>

          {/* Conversion Table */}
          <div className={`${cardClass} rounded-2xl border p-6 mb-8`}>
            <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Tabela przeliczeniowa litry na galony
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={darkMode ? 'bg-slate-700' : 'bg-blue-50'}>
                    <th className={`px-4 py-2 text-left ${darkMode ? 'text-cyan-300' : 'text-blue-900'}`}>Litry</th>
                    <th className={`px-4 py-2 text-right ${darkMode ? 'text-cyan-300' : 'text-blue-900'}`}>Galony</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 5, 10, 15, 20, 30, 40, 50, 75, 100].map((lit) => (
                    <tr key={lit} className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                      <td className={`px-4 py-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{lit} L</td>
                      <td className={`px-4 py-2 text-right font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {(lit / 3.78541).toFixed(2)} gal
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fuel Price Table */}
          <div className="mb-8">
            <FuelPriceTable darkMode={darkMode} dollarRate={dollarRate} lang="pl" />
          </div>

          {/* SEO Content */}
          <SEOContent darkMode={darkMode} title="Jak przeliczyć litry na galony?">
            <h3 className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Wzór na przeliczanie litrów na galony
            </h3>
            <p>
              Przeliczanie litrów na galony amerykańskie wymaga podzielenia liczby litrów przez współczynnik <strong>3,78541</strong>.
              Można też pomnożyć przez <strong>0,264172</strong>. Dla szybkich obliczeń w głowie używaj wartości 0,26 lub po prostu dziel przez 4 i dodaj 5%.
            </p>
            <p className="mt-3">
              <strong>Wzór:</strong> Galony = Litry ÷ 3,78541 <br />
              <strong>Alternatywnie:</strong> Galony = Litry × 0,264172
            </p>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Dlaczego warto znać przelicznik?
            </h3>
            <p>
              Znajomość przelicznika litrów na galony jest niezbędna podczas podróży po USA. Na amerykańskich stacjach benzynowych
              ceny paliwa podawane są w dolarach za galon. Znając przelicznik, możesz łatwo obliczyć, ile paliwa potrzebujesz
              i porównać ceny z polskimi.
            </p>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Przykłady przeliczenia
            </h3>
            <ul className={`list-disc list-inside mt-2 space-y-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <li><strong>50 litrów</strong> = 13,21 galona (typowy zbiornik europejskiego samochodu)</li>
              <li><strong>60 litrów</strong> = 15,85 galona (zbiornik SUV-a)</li>
              <li><strong>80 litrów</strong> = 21,13 galona (zbiornik większego pojazdu)</li>
            </ul>

            <h3 className={`font-bold text-lg mb-2 mt-6 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Porady dla podróżujących po USA
            </h3>
            <p>
              Planując podróż samochodem po Stanach, warto wiedzieć że typowy amerykański samochód ma zbiornik paliwa o pojemności
              12-18 galonów (45-68 litrów). Wynajmując auto, sprawdź pojemność zbiornika w specyfikacji - pomoże Ci to zaplanować
              przystanki na tankowanie podczas road tripu.
            </p>
          </SEOContent>
        </div>
      </Layout>
    </>
  );
};

export default LitersToGallons;
